/**
 * Typed SPA data-layer errors. HttpRepository maps wire codes here;
 * components render message, never stack traces.
 */
export type DataErrorCode =
  | 'NETWORK'
  | 'VALIDATION'
  | 'NOT_FOUND'
  | 'UNAUTHORIZED'
  | 'RATE_LIMITED'
  | 'NO_SNAPSHOT'
  | 'DELISTED'
  | 'INTERNAL'
  | 'UNKNOWN';

export interface DataErrorLifecycle {
  readonly canonicalTicker?: string;
  readonly lastSeenOn?: string;
}

export class DataError extends Error {
  public readonly kind = 'data-error' as const;
  public readonly canonicalTicker: string | undefined;
  public readonly lastSeenOn: string | undefined;

  public constructor(
    public readonly code: DataErrorCode,
    message: string,
    cause?: unknown,
    lifecycle?: DataErrorLifecycle,
  ) {
    super(message, cause !== undefined ? { cause } : undefined);
    this.name = 'DataError';
    this.canonicalTicker = lifecycle?.canonicalTicker;
    this.lastSeenOn = lifecycle?.lastSeenOn;
  }
}

export function isDataError(error: unknown): error is DataError {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { kind?: unknown }).kind === 'data-error' &&
    typeof (error as { code?: unknown }).code === 'string'
  );
}

/** Map API error codes to DataErrorCode. */
export function mapApiErrorCode(code: string): DataErrorCode {
  switch (code) {
    case 'VALIDATION_FAILED':
      return 'VALIDATION';
    case 'NOT_FOUND':
      return 'NOT_FOUND';
    case 'UNAUTHENTICATED':
      return 'UNAUTHORIZED';
    case 'RATE_LIMITED':
      return 'RATE_LIMITED';
    case 'NO_PUBLISHED_SNAPSHOT':
      return 'NO_SNAPSHOT';
    case 'SECURITY_DELISTED':
      return 'DELISTED';
    case 'INTERNAL':
      return 'INTERNAL';
    case 'PAYLOAD_TOO_LARGE':
      return 'UNKNOWN';
    default:
      return 'UNKNOWN';
  }
}
